"use client"

import { useEffect } from "react"
import { marketBaseOf } from "@local/i18n/src/languages"
import { CART_VERSION } from "./cart/const"
import { getOrCreateCartManager } from "./cart/cartManagerRegistry"
import type { Cart } from "./cart/types"
import { hasCleanupCooldown, setCleanupCooldown } from "./utils/cleanupCooldown"

interface CartCleanupControllerProps {
  market: string
}

export const CartCleanupController = ({
  market,
}: CartCleanupControllerProps) => {
  useEffect(() => {
    // Check if cleanup was done recently (cooldown active)
    if (hasCleanupCooldown()) {
      return
    }

    // DEV-9850: stored cart state is keyed by base market (CartStorage/
    // CartManager normalize via marketBaseOf), so `data.region` is always a
    // base slug ("de"). Comparing against a raw language-variant slug
    // ("de-en") would never match, silently disabling cleanup on variant
    // pages. Normalize here too.
    cleanupConvertedCarts(marketBaseOf(market))
  }, [market])

  return null
}

// Exported for tests. The component wrapper above is only the cooldown gate.
export const cleanupConvertedCarts = (market: string): void => {
  if (typeof localStorage === "undefined") return

  const prefix = `cart_v2_r${CART_VERSION}_`
  const keys = Object.keys(localStorage).filter((k) => k.startsWith(prefix))

  let cleanedCount = 0

  for (const key of keys) {
    try {
      const data = JSON.parse(
        localStorage.getItem(key) || "{}"
      ) as Cart.StateData

      if (data.convertedCart && data.region === market) {
        // Clear through the registry so the live singleton every mounted
        // component (header badge, cart drawer, useCart) subscribes to is
        // emptied too. A throwaway CartManager only wiped storage and left
        // the in-memory items behind: a later applyCoupon/saveToStorage on
        // the singleton re-persisted the purchased items under the original,
        // already-converted cart id (DEV-10004).
        const cart = getOrCreateCartManager(market, data.channel)
        cart.clearCart({ noTrack: true })

        cleanedCount++
      }
    } catch (_e) {
      localStorage.removeItem(key)
    }
  }

  if (cleanedCount > 0) {
    console.info(
      `Cleaned ${cleanedCount} converted cart(s) for market: ${market}`
    )
  }

  setCleanupCooldown()
}
